import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Bell, Mail, Smartphone, Loader2, RefreshCw, CheckCircle, XCircle, Edit, Search, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationPrefsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editPrefs, setEditPrefs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load notification preferences');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (pref) => {
    setEditingUser(pref);
    setEditPrefs({
      email_enabled: pref.email_enabled !== false,
      push_enabled: pref.push_enabled !== false,
      marketing_enabled: pref.marketing_enabled !== false,
      updates_enabled: pref.updates_enabled !== false,
      reservation_notifications: pref.reservation_notifications !== false,
      message_notifications: pref.message_notifications !== false,
      collaboration_notifications: pref.collaboration_notifications !== false
    });
  };

  const handleSavePrefs = async () => {
    if (!editingUser) return;
    
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/notification-preferences/${editingUser.user_id}`,
        editPrefs,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notification preferences updated successfully');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update preferences');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const filteredPrefs = data?.preferences?.filter(pref => 
    !search || pref.user_email?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Notification Preferences</h1>
          <p className="text-gray-600 mt-2">View and manage user notification settings</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Users with Preferences</p>
                <p className="text-3xl font-bold">{data?.total_users_with_prefs || 0}</p>
              </div>
              <Bell className="w-10 h-10 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Email Enabled</p>
                <p className="text-3xl font-bold text-green-600">{data?.email_enabled_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Email Disabled</p>
                <p className="text-3xl font-bold text-red-600">{data?.email_disabled_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Push Enabled</p>
                <p className="text-3xl font-bold text-blue-600">{data?.push_enabled_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferences Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Preferences</CardTitle>
          <CardDescription>Click edit to modify individual user notification settings</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {filteredPrefs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No preference data found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Push</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Marketing</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Updates</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredPrefs.map((pref, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {pref.user_email || 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline">{pref.user_role?.toUpperCase() || 'N/A'}</Badge>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {pref.email_enabled !== false ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {pref.push_enabled !== false ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {pref.marketing_enabled !== false ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {pref.updates_enabled !== false ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditUser(pref)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Notification Preferences</DialogTitle>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4 py-4">
              <div className="text-sm text-gray-600 mb-4">
                <strong>User:</strong> {editingUser.user_email}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-xs text-gray-500">Receive notifications via email</p>
                  </div>
                  <Switch
                    checked={editPrefs.email_enabled}
                    onCheckedChange={(checked) => setEditPrefs({...editPrefs, email_enabled: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Push Notifications</p>
                    <p className="text-xs text-gray-500">Browser push notifications</p>
                  </div>
                  <Switch
                    checked={editPrefs.push_enabled}
                    onCheckedChange={(checked) => setEditPrefs({...editPrefs, push_enabled: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Marketing Emails</p>
                    <p className="text-xs text-gray-500">Receive marketing communications</p>
                  </div>
                  <Switch
                    checked={editPrefs.marketing_enabled}
                    onCheckedChange={(checked) => setEditPrefs({...editPrefs, marketing_enabled: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Platform Updates</p>
                    <p className="text-xs text-gray-500">News about new features</p>
                  </div>
                  <Switch
                    checked={editPrefs.updates_enabled}
                    onCheckedChange={(checked) => setEditPrefs({...editPrefs, updates_enabled: checked})}
                  />
                </div>

                <hr className="my-2" />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Reservation Alerts</p>
                    <p className="text-xs text-gray-500">New reservations & status changes</p>
                  </div>
                  <Switch
                    checked={editPrefs.reservation_notifications}
                    onCheckedChange={(checked) => setEditPrefs({...editPrefs, reservation_notifications: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Message Alerts</p>
                    <p className="text-xs text-gray-500">New messages received</p>
                  </div>
                  <Switch
                    checked={editPrefs.message_notifications}
                    onCheckedChange={(checked) => setEditPrefs({...editPrefs, message_notifications: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Collaboration Alerts</p>
                    <p className="text-xs text-gray-500">Contract & collaboration updates</p>
                  </div>
                  <Switch
                    checked={editPrefs.collaboration_notifications}
                    onCheckedChange={(checked) => setEditPrefs({...editPrefs, collaboration_notifications: checked})}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleSavePrefs} disabled={saving} style={{ backgroundColor: '#62D84E' }}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationPrefsPage;
